import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Avatar,
  Radio,
  IconButton
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const DAY_WIDTH = 35;
const ROW_HEIGHT = 42;
const HEADER_HEIGHT = 70;

// ================= DATE GENERATOR (REAL CALENDAR) =================
const generateDates = (startDate, days) => {
  const dates = [];
  let current = new Date(startDate);

  for (let i = 0; i < days; i++) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
};

const dates = generateDates("2024-03-01", 60);

// ================= DATA =================
const initialData = [
  {
    id: "g1",
    group: "Research",
    open: true,
    items: [
      { id: "t1", name: "Market Research", duration: 7, start: 2, color: "#4ecdc4", users: ["A","B"], progress:80 },
      { id: "t2", name: "Competitor Analysis", duration: 7, start: 5, color: "#66bb6a", users: ["C"], progress:30 },
      { id: "t3", name: "Goal Definition", duration: 5, start: 9, color: "#ffa726", users: ["D","E"], progress:40 }
    ]
  },
  {
    id: "g2",
    group: "Design",
    open: true,
    items: [
      { id: "t4", name: "Wireframing", duration: 5, start: 12, color: "#ef5350", users: ["F"], progress:30 },
      { id: "t5", name: "UI Design", duration: 10, start: 15, color: "#ab47bc", users: ["G"], progress:40 }
    ]
  },
  {
    id: "g3",
    group: "Development",
    open: true,
    items: [
      { id: "t6", name: "HTML Coding", duration: 14, start: 20, color: "#26a69a", users: ["H"], progress:100 },
      { id: "t7", name: "CSS Styling", duration: 10, start: 22, color: "#ffa726", users: ["I"], progress:100 }
    ]
  }
];

export default function GanttView() {

  const [data, setData] = useState(initialData);
  const [selectedTask, setSelectedTask] = useState(null);
  const [dragBar, setDragBar] = useState(null);

  // ================= BUILD ROWS =================
  const rows = [];
  let visibleIndex = 0;

  data.forEach(group => {
    rows.push({ type: "group", group });

    if (group.open) {
      group.items.forEach(task => {
        rows.push({
          type: "task",
          task,
          groupId: group.id,
          visualIndex: visibleIndex++
        });
      });
    }
  });

  // ================= GROUP TOGGLE =================
  const toggleGroup = (id) => {
    setData(prev =>
      prev.map(g => g.id === id ? { ...g, open: !g.open } : g)
    );
  };

  // ================= DRAG (HORIZONTAL FIXED) =================
  const handleBarMouseDown = (e, task) => {
    setDragBar({
      id: task.id,
      startX: e.clientX,
      originalStart: task.start
    });
  };

  const handleMouseMove = (e) => {
    if (!dragBar) return;

    const diff = e.clientX - dragBar.startX;
    const days = Math.round(diff / DAY_WIDTH);

    setData(prev =>
      prev.map(g => ({
        ...g,
        items: g.items.map(t =>
          t.id === dragBar.id
            ? { ...t, start: Math.max(0, dragBar.originalStart + days) }
            : t
        )
      }))
    );
  };

  const handleMouseUp = () => setDragBar(null);

  return (
    <Box
      p={3}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      sx={{ height: "100vh", overflow: "hidden" }}
    >
      <Typography variant="h5" mb={2}>
        Project Gantt
      </Typography>

      <Paper sx={{ height: "80vh", display: "flex" }}>

        {/* ================= LEFT ================= */}
        <Box sx={{ width: "30%", borderRight: "1px solid #eee" }}>

          <Box display="flex" p={1}>
            <Typography width="50%">Title</Typography>
            <Typography width="20%">Duration</Typography>
            <Typography width="30%">Status</Typography>
          </Box>

          {rows.map((row) => {

            if (row.type === "group") {

              const totalDuration = row.group.items.reduce(
                (sum, t) => sum + t.duration,
                0
              );

              return (
                <Box key={row.group.id} display="flex" alignItems="center">
                  <IconButton size="small" onClick={() => toggleGroup(row.group.id)}>
                    {row.group.open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                  </IconButton>

                  <Typography width="50%" fontWeight="bold">
                    {row.group.group}
                  </Typography>

                  <Typography width="20%" fontWeight="bold">
                    {totalDuration}d
                  </Typography>

                  <Box width="30%">
                    <LinearProgress value={70} variant="determinate" sx={{ height: 6 }} />
                  </Box>
                </Box>
              );
            }

            const t = row.task;

            return (
              <Box
                key={t.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: ROW_HEIGHT,
                  pl: 3,
                  background: selectedTask === t.id ? "#e3f2fd" : ""
                }}
              >
                <Radio
                  checked={selectedTask === t.id}
                  onClick={() => setSelectedTask(t.id)}
                  size="small"
                />

                <Typography width="50%">{t.name}</Typography>
                <Typography width="20%">{t.duration}d</Typography>

                <Box width="30%">
                  <LinearProgress value={t.progress} variant="determinate" />
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* ================= RIGHT ================= */}
        <Box sx={{ flex: 1, overflow: "auto" }}>

          <Box sx={{ minWidth: dates.length * DAY_WIDTH }}>

            {/* ===== MONTH + DATE HEADER ===== */}
            <Box position="sticky" top={0} bgcolor="#fff" zIndex={5} pb={1}>

              {/* MONTH ROW */}
              <Box display="flex">
                {dates.map((d, i) => {
                  const show = i === 0 || d.getDate() === 1;

                  return (
                    <Box key={i} width={DAY_WIDTH} textAlign="center">
                      {show && (
                        <Typography fontSize={11} fontWeight="bold">
                          {d.toLocaleString("default", {
                            month: "long",
                            year: "numeric"
                          })}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* DATE ROW */}
              <Box display="flex">
                {dates.map((d, i) => (
                  <Box key={i} width={DAY_WIDTH} textAlign="center" fontSize={11}>
                    {d.getDate()}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* ===== BODY ===== */}
            <Box sx={{ position: "relative", height: rows.length * ROW_HEIGHT }}>

{rows.map((row, index) => {

  if (row.type !== "group") return null;

  const group = row.group;
  const firstTask = group.items[0];
  if (!firstTask) return null;

  const lastEnd = Math.max(
    ...group.items.map(t => t.start + t.duration)
  );

  return (
    <Box
      key={group.id}
      sx={{
        position: "absolute",
        top: HEADER_HEIGHT + index * ROW_HEIGHT,
        left: firstTask.start * DAY_WIDTH,
        width: (lastEnd - firstTask.start) * DAY_WIDTH,
        height: 2,
        background: "#999"
      }}
    >
      <Typography
        sx={{
          position: "absolute",
          top: -16,
          fontSize: 11,
          color: "#666"
        }}
      >
        {group.group}
      </Typography>
    </Box>
  );
})}

              {/* TASK BARS */}
              {rows.map((row) => {
                if (row.type !== "task") return null;

                const t = row.task;

                return (
                  <Box
                    key={t.id}
                    onMouseDown={(e) => handleBarMouseDown(e, t)}
                    sx={{
                      position: "absolute",
                      top: HEADER_HEIGHT + row.visualIndex * ROW_HEIGHT,
                      left: t.start * DAY_WIDTH,
                      width: t.duration * DAY_WIDTH,
                      height: 30,
                      background: t.color,
                      borderRadius: 20,
                      display: "flex",
                      alignItems: "center",
                      px: 1,
                      color: "#fff",
                      cursor: "grab"
                    }}
                  >
                    {t.users.map((u, i) => (
                      <Avatar key={i} sx={{ width: 20, height: 20, fontSize: 10 }}>
                        {u}
                      </Avatar>
                    ))}
                    &nbsp;{t.name}
                  </Box>
                );
              })}

            </Box>
          </Box>
        </Box>

      </Paper>
    </Box>
  );
}